import { useState, useEffect } from 'react'
import { supabase } from '../../../lib/supabase'

interface DashboardFormProps {
  dashboardId?: string | null
  onClose: () => void
  onSuccess: () => void
}

interface FormData {
  name: string
  slug: string
  description: string
  is_published: boolean
  is_default: boolean
   category_id: string | null 
  subcategory_id: string | null  
}

export function DashboardForm({ dashboardId, onClose, onSuccess }: DashboardFormProps) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    slug: '',
    description: '',
    is_published: false,
    is_default: false,
    category_id: null, 
    subcategory_id: null 
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [slugError, setSlugError] = useState<string | null>(null)
  const [categories, setCategories] = useState<any[]>([])
  const [subcategories, setSubcategories] = useState<any[]>([])

  const isEdit = Boolean(dashboardId)

  useEffect(() => {
    if (dashboardId) {
      loadDashboard()
    }
  }, [dashboardId])

  useEffect(() => {
    loadCategories()
  }, [])

  const loadDashboard = async () => {

    console.log("asdcsd");
    if (!dashboardId) return

    try {
      // @ts-ignore
      const { data, error } = await supabase
        .from('dashboards')
        .select('*')
        .eq('id', dashboardId)
        .single()

      if (error) throw error

      if (data) {
        // @ts-ignore - Supabase types issue
        const dashboard = data as any
        setFormData({
          name: dashboard.name,
          slug: dashboard.slug,
          description: dashboard.description || '',
          is_published: dashboard.is_published,
          is_default: dashboard.is_default,
          category_id: dashboard.category_id || null,
          subcategory_id: dashboard.subcategory_id || null
        })

        if (dashboard.category_id) {
          loadSubcategories(dashboard.category_id)
        }
      }
    } catch (err) {
      console.error('Error cargando dashboard:', err)
      setError('Error al cargar el dashboard')
    }
  }

  const loadCategories = async () => {
    const { data, error } = await supabase
      .from('categories')
      .select('id, name')
      .is('parent_id', null)

    if (!error) setCategories(data || [])
  }

  const loadSubcategories = async (categoryId: string) => {
    const { data, error } = await supabase
      .from('categories')
      .select('id, name')
      .eq('parent_id', categoryId)

    if (!error) setSubcategories(data || [])
  }

  const generateSlug = (name: string): string => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[áàäâ]/g, 'a')
      .replace(/[éèëê]/g, 'e')
      .replace(/[íìïî]/g, 'i')
      .replace(/[óòöô]/g, 'o')
      .replace(/[úùüû]/g, 'u')
      .replace(/ñ/g, 'n')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
  }

  const validateSlug = async (slug: string): Promise<boolean> => {
    if (!slug) {
      setSlugError('El slug es requerido')
      return false
    }

    if (!/^[a-z0-9-]+$/.test(slug)) {
      setSlugError('El slug solo puede contener letras minúsculas, números y guiones')
      return false
    }

    try {
      // @ts-ignore
      const { data, error } = await supabase
        .from('dashboards')
        .select('id')
        .eq('slug', slug)

      if (error) throw error

      // Si estamos editando, permitir el mismo slug
      const duplicates = data?.filter((d: any) => d.id !== dashboardId) || []

      if (duplicates.length > 0) {
        setSlugError('Este slug ya está en uso. Elige otro.')
        return false
      }

      setSlugError(null)
      return true
    } catch (err) {
      console.error('Error validando slug:', err)
      setSlugError('Error al validar el slug')
      return false
    }
  }

  const handleNameChange = (name: string) => {
    setFormData(prev => ({
      ...prev,
      name,
      // Auto-generate slug solo si no estamos editando
      slug: isEdit ? prev.slug : generateSlug(name)
    }))
  }

  const handleSlugChange = (slug: string) => {
    setFormData(prev => ({ ...prev, slug }))
    setSlugError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!formData.name.trim()) {
      setError('El nombre es requerido')
      return
    }

    const slugValid = await validateSlug(formData.slug)
    if (!slugValid) return

    try {
      setLoading(true)

      const { data: { user } } = await supabase.auth.getUser()

      if (isEdit) {
        // UPDATE
        const query = supabase.from('dashboards');
        const { error: updateError } = await query
          // @ts-ignore
          .update({
            name: formData.name.trim(),
            slug: formData.slug.trim(),
            description: formData.description.trim() || null,
            is_published: formData.is_published,
            is_default: formData.is_default,
            category_id: formData.category_id,
            subcategory_id: formData.subcategory_id,
          })
          // @ts-ignore
          .eq('id', dashboardId)

        if (updateError) throw updateError

        // Si se marcó como default, usar la función RPC que supera RLS
        if (formData.is_default && dashboardId) {
          // @ts-ignore - RPC function not in generated types
          const { error: rpcError } = await supabase.rpc('set_dashboard_as_default', {
            dashboard_id: dashboardId
          })
          if (rpcError) {
            console.error('Error en RPC set_dashboard_as_default:', rpcError)
            throw rpcError
          }
        }
      } else {
        // INSERT
        const query = supabase.from('dashboards')
        const { data: newDashboard, error: insertError } = await query
          // @ts-ignore
          .insert({
            name: formData.name.trim(),
            slug: formData.slug.trim(),
            description: formData.description.trim() || null,
            is_published: formData.is_published,
            is_default: formData.is_default,
            created_by: user?.id || null,
            category_id: formData.category_id,
            subcategory_id: formData.subcategory_id,
          })
          // @ts-ignore
          .select()
          .single()

        if (insertError) throw insertError

        // Si se marcó como default, usar la función RPC que supera RLS
        if (formData.is_default && newDashboard) {
          // @ts-ignore - RPC function not in generated types
          const { error: rpcError } = await supabase.rpc('set_dashboard_as_default', {
            // @ts-ignore
            dashboard_id: newDashboard.id
          })
          if (rpcError) {
            console.error('Error en RPC set_dashboard_as_default:', rpcError)
            throw rpcError
          }
        }
      }

      onSuccess()
    } catch (err: any) {
      console.error('Error guardando dashboard:', err)
      setError(err.message || 'Error al guardar el dashboard')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-6">
          {isEdit ? 'Editar Dashboard' : 'Crear Nuevo Dashboard'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Nombre */}
          <div>
            <label className="block font-medium mb-2">
              Nombre del Dashboard <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="Ej: Dashboard Forestal Provincial"
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
            />
          </div>

          {/* Categoría */}
          <div>
            <label className="block font-medium mb-2">Categoría</label>
            <select
              className="w-full border-2 border-gray-300 rounded-lg px-4 py-2"
              value={formData.category_id || ''}
              onChange={(e) => {
                const id = e.target.value
                setFormData({ ...formData, category_id: id, subcategory_id: null })
                loadSubcategories(id)
              }}
            >
              <option value="">Seleccionar categoría...</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Subcategoría */}
          <div>
            <label className="block font-medium mb-2">Subcategoría</label>
            <select
              className="w-full border-2 border-gray-300 rounded-lg px-4 py-2"
              value={formData.subcategory_id || ''}
              onChange={(e) =>
                setFormData({ ...formData, subcategory_id: e.target.value })
              }
              disabled={!formData.category_id}
            >
              <option value="">Seleccionar subcategoría...</option>
              {subcategories.map((sub) => (
                <option key={sub.id} value={sub.id}>
                  {sub.name}
                </option>
              ))}
            </select>
          </div>

          {/* Slug */}
          <div>
            <label className="block font-medium mb-2">
              Slug (URL) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              className={`w-full border-2 rounded-lg px-4 py-2 font-mono text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                slugError ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="dashboard-forestal-provincial"
              value={formData.slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              onBlur={() => validateSlug(formData.slug)}
            />
            {slugError ? (
              <p className="text-sm text-red-600 mt-1">{slugError}</p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">
                URL: <code className="bg-gray-100 px-2 py-1 rounded">?dashboard={formData.slug}</code>
              </p>
            )}
          </div>

          {/* Descripción */}
          <div>
            <label className="block font-medium mb-2">Descripción</label>
            <textarea
              className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              rows={3}
              placeholder="Descripción breve del dashboard..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>

          {/* Opciones */}
          <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="is_published"
                checked={formData.is_published}
                onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                className="mt-1 w-4 h-4 text-green-600 rounded focus:ring-green-500"
              />
              <div className="flex-1">
                <label htmlFor="is_published" className="font-medium cursor-pointer">
                  Publicar dashboard
                </label>
                <p className="text-sm text-gray-600">
                  {formData.is_published
                    ? '✓ El dashboard será visible públicamente'
                    : '⚠️ El dashboard solo será visible para administradores'}
                </p>
              </div>
            </div>
          </div>

          {/* Error general */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}

          {/* Botones */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border-2 border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || Boolean(slugError)}
              className="flex-1 bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Guardando...' : isEdit ? 'Actualizar Dashboard' : 'Crear Dashboard'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
